import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { upgradeDevicesQuery, upgradesPageQuery, upgradesTableQuery, type UpgradeDevice } from '../api/inventoree'
import { AnimatedNumber } from '../alarms/AlarmDetails'
import { DataTable, useTableState } from '../ui/DataTable'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { upgradeColumns } from './upgrades/columns'

/**
 * Inventoree :: Upgrades - macheta inventoree/alarms/index.php + dashboard.php
 * + alarmTabs.php + devicesAlarms.php.
 */

const TABLE_ID = 'inventoreeAlarms'

const TILES = [
  { key: 'critical' as const, label: 'Critical', tone: 'red-intense', icon: 'fas fa-exclamation-circle' },
  { key: 'major' as const, label: 'Major', tone: 'yellow', icon: 'fas fa-exclamation-triangle' },
  { key: 'minor' as const, label: 'Minor', tone: 'green-haze', icon: 'fa fa-bell' },
  { key: 'info' as const, label: 'Info', tone: 'blue-madison', icon: 'fas fa-info-circle' },
]

function deviceIcon(type: number | string): string {
  switch (String(type)) {
    case '2':
      return 'fas fa-laptop'
    case '3':
      return 'fas fa-server'
    case '11':
      return 'fas fa-mobile-alt'
    case '12':
      return 'fas fa-tablet-alt'
    default:
      return 'fas fa-desktop'
  }
}

export function UpgradeTile({ alarm }: { alarm: UpgradeDevice }) {
  const owner = alarm.Nume ? `${alarm.Prenume ?? ''} ${alarm.Nume}` : ''
  const value = alarm.value !== '' && alarm.value != null ? `${alarm.value}${alarm.sign ?? ''}` : ''
  return (
    <div className="tile bg-red-sunglo">
      <div className="tile-body">
        <i className={deviceIcon(alarm.device_type)}></i>
        <p>{alarm.Denumire}</p>
        {alarm.Nume ? (
          <em>
            <i className="fa fa-user"></i> {owner}
          </em>
        ) : null}
      </div>
      <div className="tile-object">
        <div className="name">{value}</div>
      </div>
    </div>
  )
}

function UpgradeDevices({ id }: { id: number }) {
  const q = useQuery(upgradeDevicesQuery(id))
  if (q.isPending) return <Spinner />
  if (q.error) {
    return (
      <div className="col-md-12">
        <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
      </div>
    )
  }
  const alarms = q.data?.alarms ?? []
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          <li>
            <a href="#" className="action_button">
              Upgrades
            </a>
          </li>
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {alarms.length === 0 ? (
            <div className="col-md-12">
              <div className="alert alert-info">
                <i className="fas fa-exclamation-circle"></i> No active upgrades.
              </div>
            </div>
          ) : (
            alarms.map((a) => <UpgradeTile key={a.ID} alarm={a} />)
          )}
        </div>
      </div>
    </div>
  )
}

export function UpgradesPage() {
  const page = useQuery(upgradesPageQuery())
  const columns = useMemo(() => upgradeColumns(), [])
  const [state, setState] = useTableState(TABLE_ID, { start: 0, length: 25, order: { column: 5, dir: 'desc' }, search: '' })
  const orderColumn = columns.find((c) => c.index === state.order.column) ?? columns[5]

  const table = useQuery({
    ...upgradesTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: orderColumn.index,
      orderName: orderColumn.name ?? '',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  return (
    <Content>
      <div className="alarms-dashboard row">
        {TILES.map((tile) => (
          <div className="col-lg-3" key={tile.key}>
            <div className={`dashboard-stat ${tile.tone}`}>
              <div className="visual">
                <i className={tile.icon}></i>
              </div>
              <div className="details">
                <AnimatedNumber value={page.data?.counters[tile.key] ?? 0} />
                <div className="desc">{tile.label}</div>
              </div>
              <div className="more">Upgrades</div>
            </div>
          </div>
        ))}
      </div>

      <DataTable
        id={TABLE_ID}
        columnTools
        columns={columns}
        rows={(table.data?.data ?? []) as unknown as Record<string, unknown>[]}
        total={table.data?.recordsTotal ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId)}
        footerButtons={{ copy: true, colvis: true, refresh: () => void table.refetch() }}
        details={(row) => <UpgradeDevices id={Number(row.ID)} />}
      />
    </Content>
  )
}
